import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { toast } from 'react-toastify';
import {
    PencilSquareIcon,
    CheckIcon,
    XMarkIcon,
    UserGroupIcon,
    BuildingOfficeIcon,
    MapPinIcon,
    BriefcaseIcon,
    UserIcon,
    ShieldCheckIcon,
    GiftIcon,
    CalendarDaysIcon,
    UserPlusIcon,
    TrashIcon,
    NoSymbolIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const AdminPanel = () => {
    const [users, setUsers] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);

    // Grant Modal State
    const [showGrantModal, setShowGrantModal] = useState(false);
    const [grantableTypes, setGrantableTypes] = useState([]);
    const [grantData, setGrantData] = useState({ user_id: null, leave_type_id: '', days: 1, reason: '', userName: '' });

    // Create User Modal State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createData, setCreateData] = useState({ full_name: '', email: '', password: '', role: 'Employee', country: 'India', reporting_manager_id: '', second_approver_id: '' });
    const [isCreating, setIsCreating] = useState(false);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({ show: false, type: null, user: null });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const usersRes = await api.get('/admin/users');
            setUsers(usersRes.data);

            const settingsRes = await api.get('/settings?country=All');

            const targetTypes = settingsRes.data.leaveTypes.filter(t =>
                t.is_comp_off === true ||
                t.is_carry_forward === true ||
                t.is_probation === true ||
                t.name.toLowerCase().includes('earned') ||
                t.name.toLowerCase().includes('privilege') ||
                t.name.toLowerCase().includes('comp') ||
                t.name.toLowerCase().includes('probation')
            );

            setGrantableTypes(targetTypes);

            try {
                setCountryList(['India', 'USA', 'UK', 'Netherlands', 'UAE', 'Global']);
            } catch (e) {
                setCountryList(['India', 'USA', 'Global']);
            }

            setLoading(false);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load user data");
            setLoading(false);
        }
    };

    const startEdit = (user) => {
        setEditingUser(user.id);

        // Format Date for Input (YYYY-MM-DD)
        const formattedDate = user.created_at
            ? new Date(user.created_at).toISOString().split('T')[0]
            : '';

        setFormData({
            full_name: user.full_name || '',
            email: user.email || '',
            role: user.role,
            department: user.department || 'General',
            country: user.country || 'India',
            employment_status: user.employment_status || 'Confirmed',
            reporting_manager_id: user.reporting_manager_id || '',
            second_approver_id: user.second_approver_id || '',
            created_at: formattedDate // Initialize Date
        });
    };

    const saveEdit = async (id) => {
        try {
            await api.put('/admin/users/update', { id, ...formData });
            toast.success("User Updated Successfully");

            // Update local state
            setUsers(users.map(u => u.id === id ? { ...u, ...formData } : u));
            setEditingUser(null);
        } catch (err) {
            toast.error("Update Failed: " + (err.response?.data?.error || err.message));
        }
    };

    // --- GRANT BALANCE ---
    const openGrantModal = (user) => {
        if (grantableTypes.length === 0) return toast.warning("No grantable leave types found.");

        setGrantData({
            user_id: user.id,
            leave_type_id: grantableTypes[0].id,
            days: 1,
            reason: '',
            userName: user.full_name
        });
        setShowGrantModal(true);
    };

    const handleGrantSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post('/leaves/grant-balance', {
                user_id: grantData.user_id,
                leave_type_id: grantData.leave_type_id,
                days: grantData.days,
                reason: grantData.reason
            });
            toast.success(`Leaves granted to ${grantData.userName}`);
            setShowGrantModal(false);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to grant");
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            await api.post('/admin/users/create', createData);
            toast.success(`User ${createData.full_name} created successfully`);
            setShowCreateModal(false);
            setCreateData({ full_name: '', email: '', password: '', role: 'Employee', country: 'India', reporting_manager_id: '', second_approver_id: '' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to create user");
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteUser = async (user) => {
        try {
            await api.delete(`/admin/users/${user.id}`);
            toast.success(`${user.full_name} has been removed`);
            setConfirmModal({ show: false, type: null, user: null });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to delete user');
        }
    };

    const handleToggleStatus = async (user) => {
        try {
            const res = await api.patch(`/admin/users/${user.id}/status`);
            toast.success(`${user.full_name} is now ${res.data.employment_status}`);
            setConfirmModal({ show: false, type: null, user: null });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update status');
        }
    };

    const openConfirm = (type, user) => setConfirmModal({ show: true, type, user });
    const closeConfirm = () => setConfirmModal({ show: false, type: null, user: null });

    const potentialApprovers = users.filter(u => u.role === 'Manager' || u.role === 'Admin' || u.role === 'Super Admin');

    const getRoleStyle = (role) => {
        switch (role) {
            case 'Super Admin': return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20';
            case 'Admin': return 'bg-purple-50 text-purple-700 ring-1 ring-purple-600/20';
            case 'Manager': return 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20';
            default: return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20';
        }
    };

    const getAvatarColor = (name) => {
        if (!name) return 'bg-gray-500';
        const colors = ['bg-indigo-500', 'bg-pink-500', 'bg-cyan-500', 'bg-orange-500', 'bg-violet-500'];
        return colors[(name?.length || 0) % colors.length];
    };

    const getNameById = (id) => {
        if (!id) return null;
        const u = users.find(user => user.id === parseInt(id));
        return u ? u.full_name : 'Unknown';
    };

    if (loading) return (
        <Layout>
            <div className="flex flex-col items-center justify-center h-[80vh] text-slate-400 animate-pulse">
                <UserGroupIcon className="h-16 w-16 mb-4 opacity-50" />
                <p className="font-medium">Loading Directory...</p>
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-800 tracking-tight">Team Directory</h1>
                    <p className="text-slate-500 mt-1 flex items-center text-sm font-medium">
                        <UserGroupIcon className="w-4 h-4 mr-1.5" />
                        Managing {users.length} active employees
                    </p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-500/20"
                >
                    <UserPlusIcon className="w-4 h-4" />
                    Add New Employee
                </button>
            </div>

            <div className="w-full overflow-x-auto pb-20">
                <table className="w-full text-left border-separate border-spacing-y-3 table-fixed min-w-[1400px] lg:min-w-0">
                    <thead>
                        <tr className="text-xs text-slate-400 uppercase tracking-wider font-bold">
                            <th className="px-4 py-2 w-[20%]">Employee</th>
                            <th className="px-4 py-2 w-[10%]">Dept & Loc</th>
                            <th className="px-4 py-2 w-[10%]">Role & Status</th>
                            <th className="px-4 py-2 w-[12%]">Joined On</th> {/* Added Column */}
                            <th className="px-4 py-2 w-[16%]">L1 Approver</th>
                            <th className="px-4 py-2 w-[16%]">L2 Approver</th>
                            <th className="px-4 py-2 w-[12%] text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm">
                        {users.map(u => {
                            const isEditing = editingUser === u.id;
                            return (
                                <tr key={u.id} className={`bg-white transition-all duration-200 group rounded-2xl ${isEditing ? 'shadow-lg ring-1 ring-indigo-500 z-10 relative' : 'hover:shadow-md'}`}>
                                    {/* 1. Name & Email */}
                                    <td className="px-4 py-4 rounded-l-2xl border-y border-l border-slate-100 group-hover:border-indigo-50/50">
                                        <div className="flex items-center overflow-hidden">
                                            <div className={`h-9 w-9 min-w-[2.25rem] rounded-full flex items-center justify-center text-white font-bold text-xs shadow-sm mr-3 flex-shrink-0 ${getAvatarColor(u.full_name)}`}>
                                                {u.full_name ? u.full_name.charAt(0) : '?'}
                                            </div>
                                            <div className="w-full">
                                                {isEditing ? (
                                                    <div className="flex flex-col gap-2">
                                                        <input
                                                            type="text"
                                                            className="w-full p-1.5 border border-indigo-300 rounded text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                                                            value={formData.full_name || ''}
                                                            onChange={e => setFormData({ ...formData, full_name: e.target.value })}
                                                            placeholder="Full Name"
                                                        />
                                                        <input
                                                            type="email"
                                                            className="w-full p-1.5 border border-indigo-300 rounded text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                                                            value={formData.email || ''}
                                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                            placeholder="Email Address"
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="truncate">
                                                        <div className="font-bold text-slate-800 truncate" title={u.full_name}>{u.full_name}</div>
                                                        <div className="text-xs text-slate-500 font-medium truncate" title={u.email}>{u.email}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>

                                    {/* 2. Dept */}
                                    <td className="px-4 py-4 border-y border-slate-100 group-hover:border-indigo-50/50">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <select className="w-full py-1 bg-slate-50 border border-slate-200 rounded text-slate-700 text-xs font-bold outline-none" value={formData.department} onChange={e => setFormData({ ...formData, department: e.target.value })}>
                                                    <option>General</option><option>IT</option><option>Sales</option><option>HR</option><option>Finance</option><option>Marketing</option><option>Management</option><option>Development</option><option>QA/QC</option>
                                                </select>
                                                <select className="w-full py-1 bg-slate-50 border border-slate-200 rounded text-slate-700 text-xs font-bold outline-none" value={formData.country} onChange={e => setFormData({ ...formData, country: e.target.value })}>
                                                    {countryList.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="flex items-center text-slate-600 font-medium truncate text-xs mb-1"><BuildingOfficeIcon className="w-3 h-3 mr-1 text-slate-400" /> {u.department || 'General'}</div>
                                                <div className="flex items-center text-slate-500 text-xs"><MapPinIcon className="w-3 h-3 mr-1 text-slate-400" /> {u.country || 'India'}</div>
                                            </div>
                                        )}
                                    </td>

                                    {/* 3. Role & Status */}
                                    <td className="px-4 py-4 border-y border-slate-100 group-hover:border-indigo-50/50">
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <select className="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 text-xs font-bold outline-none" value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}>
                                                    <option>Employee</option><option>Manager</option><option>Admin</option><option>Super Admin</option>
                                                </select>
                                                <select className="w-full px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 text-xs font-bold outline-none" value={formData.employment_status} onChange={e => setFormData({ ...formData, employment_status: e.target.value })}>
                                                    <option value="Confirmed">Confirmed</option>
                                                    <option value="Probation">Probation</option>
                                                    <option value="Notice">Notice</option>
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-1 items-start">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getRoleStyle(u.role)}`}>{u.role}</span>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${u.employment_status === 'Probation' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{u.employment_status || 'Confirmed'}</span>
                                            </div>
                                        )}
                                    </td>

                                    {/* 4. Joining Date (NEW) */}
                                    <td className="px-4 py-4 border-y border-slate-100 group-hover:border-indigo-50/50">
                                        {isEditing ? (
                                            <input
                                                type="date"
                                                className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                                                value={formData.created_at}
                                                onChange={e => setFormData({ ...formData, created_at: e.target.value })}
                                            />
                                        ) : (
                                            <div className="flex items-center text-slate-600 font-bold text-xs">
                                                <CalendarDaysIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                                                {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                                            </div>
                                        )}
                                    </td>

                                    {/* 5. Manager */}
                                    <td className="px-4 py-4 border-y border-slate-100 group-hover:border-indigo-50/50">
                                        {isEditing ? (
                                            <div className="relative">
                                                <UserIcon className="w-4 h-4 absolute left-2 top-2.5 text-slate-400 pointer-events-none" />
                                                <select className="w-full pl-8 pr-2 py-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 text-xs font-bold outline-none" value={formData.reporting_manager_id} onChange={e => setFormData({ ...formData, reporting_manager_id: e.target.value })}>
                                                    <option value="">No Manager</option>
                                                    {potentialApprovers.filter(m => m.id !== u.id).map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="flex items-center text-slate-500 text-xs truncate">
                                                {u.reporting_manager_id ? (
                                                    <span className="flex items-center bg-slate-100 px-2 py-1 rounded border border-slate-200 truncate max-w-full"><BriefcaseIcon className="w-3 h-3 mr-1 flex-shrink-0" /><span className="truncate">{getNameById(u.reporting_manager_id)}</span></span>
                                                ) : <span className="text-slate-300 italic truncate">None</span>}
                                            </div>
                                        )}
                                    </td>

                                    {/* 6. Second Approver */}
                                    <td className="px-4 py-4 border-y border-slate-100 group-hover:border-indigo-50/50">
                                        {isEditing ? (
                                            <div className="relative">
                                                <ShieldCheckIcon className="w-4 h-4 absolute left-2 top-2.5 text-orange-400 pointer-events-none" />
                                                <select className="w-full pl-8 pr-2 py-2 bg-slate-50 border border-orange-200 rounded-lg text-slate-700 text-xs font-bold outline-none" value={formData.second_approver_id} onChange={e => setFormData({ ...formData, second_approver_id: e.target.value })}>
                                                    <option value="">No 2nd Approver</option>
                                                    {potentialApprovers.filter(m => m.id !== u.id).map(m => <option key={m.id} value={m.id}>{m.full_name}</option>)}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="flex items-center text-slate-500 text-xs truncate">
                                                {u.second_approver_id ? (
                                                    <span className="flex items-center bg-orange-50 px-2 py-1 rounded border border-orange-200 text-orange-700 truncate max-w-full"><ShieldCheckIcon className="w-3 h-3 mr-1 flex-shrink-0" /><span className="truncate">{getNameById(u.second_approver_id)}</span></span>
                                                ) : <span className="text-slate-300 italic truncate">Not Assigned</span>}
                                            </div>
                                        )}
                                    </td>

                                    {/* 7. Actions */}
                                    <td className="px-4 py-4 rounded-r-2xl border-y border-r border-slate-100 group-hover:border-indigo-50/50 text-right">
                                        <div className="flex items-center justify-end space-x-2">
                                            {isEditing ? (
                                                <>
                                                    <button onClick={() => saveEdit(u.id)} className="p-1.5 bg-emerald-100 text-emerald-600 rounded-lg hover:bg-emerald-200 transition-colors shadow-sm"><CheckIcon className="w-4 h-4" /></button>
                                                    <button onClick={() => setEditingUser(null)} className="p-1.5 bg-rose-100 text-rose-600 rounded-lg hover:bg-rose-200 transition-colors shadow-sm"><XMarkIcon className="w-4 h-4" /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => openGrantModal(u)} className="p-1.5 text-amber-500 bg-amber-50 hover:bg-amber-100 rounded-lg transition-all" title="Grant Balance">
                                                        <GiftIcon className="w-5 h-5" />
                                                    </button>
                                                    <button onClick={() => startEdit(u)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all" title="Edit">
                                                        <PencilSquareIcon className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => openConfirm('toggle', u)}
                                                        className={`p-1.5 rounded-lg transition-all ${u.employment_status === 'Active' ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100' : 'text-orange-500 bg-orange-50 hover:bg-orange-100'}`}
                                                        title={u.employment_status === 'Active' ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {u.employment_status === 'Active' ? <NoSymbolIcon className="w-5 h-5" /> : <ArrowPathIcon className="w-5 h-5" />}
                                                    </button>
                                                    <button
                                                        onClick={() => openConfirm('delete', u)}
                                                        className="p-1.5 text-rose-400 bg-rose-50 hover:bg-rose-100 rounded-lg transition-all"
                                                        title="Delete"
                                                    >
                                                        <TrashIcon className="w-5 h-5" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* --- CONFIRMATION MODAL --- */}
            {confirmModal.show && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-fade-in-up">
                        <div className="flex items-center justify-center mb-4">
                            <div className={`p-3 rounded-full ${confirmModal.type === 'delete' ? 'bg-rose-100' : confirmModal.user?.employment_status === 'Active' ? 'bg-orange-100' : 'bg-emerald-100'}`}>
                                <ExclamationTriangleIcon className={`w-7 h-7 ${confirmModal.type === 'delete' ? 'text-rose-500' : 'text-orange-500'}`} />
                            </div>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 text-center mb-1">
                            {confirmModal.type === 'delete'
                                ? 'Remove Employee'
                                : confirmModal.user?.employment_status === 'Active' ? 'Deactivate Employee' : 'Activate Employee'}
                        </h3>
                        <p className="text-sm text-slate-500 text-center mb-6">
                            {confirmModal.type === 'delete'
                                ? <>Are you sure you want to remove <b>{confirmModal.user?.full_name}</b>? This action cannot be undone.</>
                                : confirmModal.user?.employment_status === 'Active'
                                    ? <>Deactivating <b>{confirmModal.user?.full_name}</b> will block their access. You can reactivate them later.</>
                                    : <>Reactivating <b>{confirmModal.user?.full_name}</b> will restore their access.</>}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={closeConfirm}
                                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => confirmModal.type === 'delete' ? handleDeleteUser(confirmModal.user) : handleToggleStatus(confirmModal.user)}
                                className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-colors ${
                                    confirmModal.type === 'delete'
                                        ? 'bg-rose-600 hover:bg-rose-700'
                                        : confirmModal.user?.employment_status === 'Active'
                                            ? 'bg-orange-500 hover:bg-orange-600'
                                            : 'bg-emerald-600 hover:bg-emerald-700'
                                }`}
                            >
                                {confirmModal.type === 'delete'
                                    ? 'Remove'
                                    : confirmModal.user?.employment_status === 'Active' ? 'Deactivate' : 'Activate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CREATE USER MODAL --- */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-in-up">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-800">Add New Employee</h3>
                            <button onClick={() => setShowCreateModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateUser} className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Full Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="John Doe"
                                    value={createData.full_name}
                                    onChange={e => setCreateData({ ...createData, full_name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="user@company.com"
                                    value={createData.email}
                                    onChange={e => setCreateData({ ...createData, email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Password</label>
                                <input
                                    type="password"
                                    required
                                    className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    placeholder="Minimum 8 characters"
                                    value={createData.password}
                                    onChange={e => setCreateData({ ...createData, password: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">Role</label>
                                    <select
                                        className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        value={createData.role}
                                        onChange={e => setCreateData({ ...createData, role: e.target.value })}
                                    >
                                        <option>Employee</option>
                                        <option>Manager</option>
                                        <option>Admin</option>
                                        <option>Super Admin</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase">Country</label>
                                    <select
                                        className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        value={createData.country}
                                        onChange={e => setCreateData({ ...createData, country: e.target.value })}
                                    >
                                        {countryList.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">L1 Approver <span className="text-rose-500">*</span></label>
                                <select
                                    required
                                    className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={createData.reporting_manager_id}
                                    onChange={e => setCreateData({ ...createData, reporting_manager_id: e.target.value })}
                                >
                                    <option value="">— Select L1 Approver —</option>
                                    {users.filter(u => u.role === 'Manager' || u.role === 'Admin' || u.role === 'Super Admin').map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">L2 Approver <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
                                <select
                                    className="w-full p-2.5 border border-slate-200 rounded-lg mt-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={createData.second_approver_id}
                                    onChange={e => setCreateData({ ...createData, second_approver_id: e.target.value })}
                                >
                                    <option value="">— None —</option>
                                    {users.filter(u => (u.role === 'Manager' || u.role === 'Admin' || u.role === 'Super Admin') && u.id !== parseInt(createData.reporting_manager_id)).map(u => <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
                                </select>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-slate-500 hover:text-slate-700 font-medium text-sm">Cancel</button>
                                <button
                                    type="submit"
                                    disabled={isCreating}
                                    className="px-5 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                                >
                                    {isCreating ? 'Adding...' : 'Add Employee'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* --- GRANT MODAL --- */}
            {showGrantModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-fade-in-up">
                        <h3 className="text-lg font-bold text-slate-800 mb-2">Grant Leave Balance</h3>
                        <p className="text-sm text-slate-500 mb-4">Adding days for: <b>{grantData.userName}</b></p>
                        <form onSubmit={handleGrantSubmit} className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Leave Type</label>
                                <select
                                    className="w-full p-2 border rounded-lg mt-1 bg-white"
                                    value={grantData.leave_type_id}
                                    onChange={e => setGrantData({ ...grantData, leave_type_id: e.target.value })}
                                >
                                    {grantableTypes.map(t => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Days to Add</label>
                                <input type="number" step="0.5" className="w-full p-2 border rounded-lg mt-1" value={grantData.days} onChange={e => setGrantData({ ...grantData, days: e.target.value })} required />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Reason</label>
                                <textarea className="w-full p-2 border rounded-lg mt-1" placeholder="e.g. Project Bonus / Weekend Work" value={grantData.reason} onChange={e => setGrantData({ ...grantData, reason: e.target.value })} required />
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setShowGrantModal(false)} className="px-4 py-2 text-slate-500">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700">Grant</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default AdminPanel;